import { useEffect, useRef, type MutableRefObject } from 'react';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import type { TreeSourcePosition } from '@confluence/tree/entry-points/tree-types.ts';

import { findSyntheticItemParentId } from './autoMoveExpansion.ts';
import type { ContentTreeItem } from './data-transformers.ts';
import type { PageTreeCoordinatorProps } from './PageTreeCoordinator.tsx';
import type { MovePageParams } from './useMovePageHandler.ts';
import type { PageTreeState, QueryChildren } from './usePageTreeState.ts';
// usePageTreeState is imported as a value because it is referenced via `typeof` below to derive the
// actions type; oxlint's consistent-type-imports misflags this as type-only, but `import type` would
// break the `typeof` query.
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- see comment above
import { usePageTreeState } from './usePageTreeState.ts';
import { VIRTUAL_LOCAL_ROOT_ID, VIRTUAL_ROOT_ID } from './virtualRootIds.js';

type PageTreeActions = ReturnType<typeof usePageTreeState>[1];

/** The tree's internal roots are not real pages and must never be used as a destination parent. */
export const isVirtualRootId = (id: string): boolean =>
	id === VIRTUAL_ROOT_ID || id === VIRTUAL_LOCAL_ROOT_ID;

/**
 * Reported as `instructionType` when `onPageMove` is called for the default destination rather than
 * a user's move, so the dialog can tell the two apart (e.g. for analytics).
 */
export const DEFAULT_DESTINATION_INSTRUCTION = 'default-destination';

export type DefaultPlacementType = 'beforeSibling' | 'afterSibling' | 'underParent' | 'topLevel';

/** Names where a default destination puts the page, for analytics. */
export const getDefaultPlacementType = (params: MovePageParams): DefaultPlacementType => {
	if ('siblingId' in params) {
		return params.positionToSibling === 'before' ? 'beforeSibling' : 'afterSibling';
	}
	return 'parentId' in params ? 'underParent' : 'topLevel';
};

export type SyntheticItemPlacement = {
	moveParams: MovePageParams;
	parent: ContentTreeItem;
	position: TreeSourcePosition;
};

/**
 * Describes where the synthetic (moving) item currently sits in the tree as the move params that
 * would put it there. Anchors to a real neighbour when there is one, so the move lands exactly where
 * the tree draws the item — `parentId` alone would append it as the last child instead.
 */
export const getSyntheticItemPlacement = (
	pages: PageTreeState['pages'],
	syntheticItemId: string,
	spaceKey: string,
): SyntheticItemPlacement | null => {
	const parentId = findSyntheticItemParentId(pages, syntheticItemId);
	const parent = parentId ? pages[parentId] : undefined;
	const children = (parent?.children ?? []).map(String);
	const index = children.indexOf(syntheticItemId);
	if (!parent || index === -1) {
		return null;
	}

	const isRealSibling = (id: string) =>
		id !== syntheticItemId && !isVirtualRootId(id) && Boolean(pages[id]);
	const nextSiblingId = children.slice(index + 1).find(isRealSibling);
	const previousSiblingId = children.slice(0, index).reverse().find(isRealSibling);

	let moveParams: MovePageParams;
	if (nextSiblingId) {
		moveParams = { pageId: syntheticItemId, siblingId: nextSiblingId, positionToSibling: 'before' };
	} else if (previousSiblingId) {
		moveParams = {
			pageId: syntheticItemId,
			siblingId: previousSiblingId,
			positionToSibling: 'after',
		};
	} else if (isVirtualRootId(String(parent.id))) {
		moveParams = { pageId: syntheticItemId, spaceKey };
	} else {
		moveParams = { pageId: syntheticItemId, parentId: String(parent.id) };
	}

	return { moveParams, parent, position: { parentId: parent.id, index } };
};

/**
 * Move-dialog only. Once the tree has loaded, picks the moving page's default destination and
 * reports it through `onPageMove` exactly once, so Move is enabled without the user dragging it.
 *
 * The default is the first child of the space homepage. A tree opened with no current page is
 * rooted above the homepage and draws the moving page as a top-level page, so it is reparented under
 * the homepage first (expanding the homepage if needed) and the resulting spot is what's reported.
 * With no homepage, the page stays at the top level of the space.
 *
 * Skipped while an assisted suggestion (`moveToParentId`) is being applied, since that flow sets its
 * own destination.
 */
export const useSelectSyntheticItemPlacementOnLoad = ({
	isEnabled,
	syntheticItem,
	moveToParentId,
	pages,
	isPageTreeLoading,
	loadedSpaceKey,
	spaceKey,
	spaceId,
	homepageId,
	actions,
	queryChildren,
	isMountedRef,
	onPageMove,
}: {
	isEnabled?: boolean;
	syntheticItem?: ContentTreeItem;
	moveToParentId?: string | null;
	pages: PageTreeState['pages'];
	isPageTreeLoading: boolean;
	loadedSpaceKey?: string | null;
	spaceKey: string;
	spaceId?: string | null;
	homepageId?: string | null;
	actions: Pick<PageTreeActions, 'getState' | 'expandPage' | 'movePageInState'>;
	queryChildren: QueryChildren;
	isMountedRef: MutableRefObject<boolean>;
	onPageMove: PageTreeCoordinatorProps['onPageMove'];
}): void => {
	const hasSelectedRef = useRef(false);
	// onPageMove is often an inline function; read the latest one when the async work finishes.
	const onPageMoveRef = useRef(onPageMove);
	useEffect(() => {
		onPageMoveRef.current = onPageMove;
	}, [onPageMove]);

	useEffect(() => {
		if (hasSelectedRef.current || !isEnabled || !syntheticItem) {
			return;
		}
		if (moveToParentId) {
			hasSelectedRef.current = true;
			return;
		}
		// Wait for this space's data — the store may briefly still hold another space's pages.
		if (isPageTreeLoading || loadedSpaceKey !== spaceKey) {
			return;
		}
		const sourceId = String(syntheticItem.id);
		if (!findSyntheticItemParentId(pages, sourceId)) {
			return;
		}
		hasSelectedRef.current = true;

		const selectDefaultPlacement = async () => {
			const homepage = homepageId ? actions.getState().pages[homepageId] : undefined;
			if (homepageId && homepage) {
				if (homepage.hasChildren && !actions.getState().expandedPages.has(homepageId)) {
					try {
						await actions.expandPage(homepage, queryChildren);
					} catch (error) {
						// Expansion is best effort; the page can still move into the homepage.
						markErrorAsHandled(error);
					}
					if (!isMountedRef.current) {
						return;
					}
				}
				const { pages: currentPages } = actions.getState();
				const currentParentId = findSyntheticItemParentId(currentPages, sourceId);
				const index = currentParentId
					? (currentPages[currentParentId]?.children?.map(String).indexOf(sourceId) ?? -1)
					: -1;
				if (currentParentId && currentParentId !== homepageId && index !== -1) {
					actions.movePageInState(
						sourceId,
						{ parentId: currentParentId, index },
						{ parentId: homepageId, index: 0 },
						queryChildren,
						true,
					);
				}
			}

			const { pages: latestPages } = actions.getState();
			const placement = getSyntheticItemPlacement(latestPages, sourceId, spaceKey);
			if (!placement || !isMountedRef.current) {
				return;
			}
			const { moveParams, parent, position } = placement;
			const item = latestPages[sourceId];
			onPageMoveRef.current(moveParams, {
				contentStatus: item?.data?.status || null,
				movedPageTitle: item?.data?.title ?? '',
				spaceId,
				sourceParent: parent,
				source: position,
				destination: position,
				instructionType: DEFAULT_DESTINATION_INSTRUCTION,
				// The dialog hasn't moved anything yet, so there is nothing to undo.
				revertMove: () => moveParams,
			});
		};
		void selectDefaultPlacement();
	}, [
		isEnabled,
		syntheticItem,
		moveToParentId,
		pages,
		isPageTreeLoading,
		loadedSpaceKey,
		spaceKey,
		spaceId,
		homepageId,
		actions,
		queryChildren,
		isMountedRef,
	]);
};
